import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { hotelAffiliationsApi } from '@/services/api'

/**
 * Pending affiliation-request rows aggregated across ALL of the user's
 * active hotel seats. Single consumer before: Sidebar badge +
 * AffiliationRequestNotification both watched user.hotels[0] only, which
 * silently dropped pending requests on seats 2..n — and 403'd the whole
 * count when that first grant was inactive (PUX-05). Per-seat failures
 * are isolated with allSettled so one bad seat can't hide the others.
 *
 * The "Pending your decision" filter mirrors #449: the pending list also
 * contains rows waiting on the affiliate, which aren't actionable for
 * the hotel and must not drive badge/notification counts.
 */
export function usePendingAffiliations(enabled = true) {
  const { user } = useAuth()

  const activeHotelIds = useMemo(
    () => (user?.hotels ?? []).filter(h => h.isActive).map(h => h.hotelId),
    [user?.hotels],
  )

  const { data } = useQuery({
    queryKey: ['pending-affiliations', activeHotelIds],
    queryFn: async () => {
      const results = await Promise.allSettled(
        activeHotelIds.map(id => hotelAffiliationsApi.getPendingAffiliations(id)),
      )
      const rows: Array<{ status?: string | null }> = []
      for (const r of results) {
        if (r.status === 'fulfilled') {
          rows.push(...((r.value.data || []) as Array<{ status?: string | null }>))
        }
      }
      return rows.filter(r => r.status === 'Pending your decision')
    },
    enabled: enabled && !!user && activeHotelIds.length > 0,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  return { pendingAffiliations: data, count: data?.length ?? 0, activeHotelIds }
}
